import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BentoBrandSearchParameters,
  BentoCategory,
  Sort,
  TargetGender,
} from "schemas/dashboard";
import { LANGUAGES, Language } from "schemas/languages";

import LocationAutocomplete from "components/LocationAutocomplete";
import {
  DiscoverSearchGeopoint,
  UserLocation,
} from "components/LocationAutocomplete/schema";

/* ── Following-size helpers ───────────────────────────────────────── */

const FOLLOWER_THRESHOLDS = [
  { value: 0, label: "No minimum" },
  { value: 10_000, label: "10K" },
  { value: 100_000, label: "100K" },
  { value: 500_000, label: "500K" },
  { value: 1_000_000, label: "1M" },
  { value: 10_000_000, label: "10M" },
];

const FOLLOWER_MAX_THRESHOLDS = [
  { value: 10_000, label: "10K" },
  { value: 100_000, label: "100K" },
  { value: 500_000, label: "500K" },
  { value: 1_000_000, label: "1M" },
  { value: 10_000_000, label: "10M" },
  { value: -1, label: "No maximum" },
];

/* ── Static content ───────────────────────────────────────────────── */

const EXAMPLE_SEARCHES = ["coffee", "bedding", "games"];

/** Parent category names where target gender filtering is relevant. */
const GENDER_RELEVANT_PARENTS = new Set([
  "Fashion",
  "Fashion Accessories",
  "Beauty & Personal Care",
  "Baby, Kids, & Family",
  "Lifestyle",
  "Health & Wellness",
  "Sports & Fitness",
]);

/** Individual subcategory names that are gender-relevant even if their parent isn't. */
const GENDER_RELEVANT_SUBCATEGORIES = new Set([
  "Beauty Services",
  "Gyms & Fitness Services",
  "Health & Wellness Services",
]);

const GENDER_OPTIONS: { key: TargetGender; name: string }[] = [
  { key: TargetGender.female, name: "Female" },
  { key: TargetGender.male, name: "Male" },
];

const TIPS = [
  'Use specific keywords like "sustainable" or "clean"',
  "Multiple values in one filter? We'll find brands matching any of them",
  "Different filter types narrow results — brands must match all of them",
  "Perfect for highly targeted brand discovery",
];

/* ── Shared sx tokens ─────────────────────────────────────────────── */

const selectSx = {
  width: "100%",
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e5e7eb",
    borderWidth: 2,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e5e7eb", borderWidth: 2 },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
  "& .MuiInputBase-input": {
    fontSize: 16,
    py: 1.5,
    px: 2,
    "&::placeholder": { color: "rgba(1,67,57,0.5)", opacity: 1 },
  },
};

const locationAutocompleteSx = {
  border: "2px solid #e5e7eb",
  borderRadius: "10px",
  height: 52,
  py: 1.5,
  px: 2,
  width: "100%",
  fontSize: 16,
  boxSizing: "border-box",
  "&:hover, &:focus": {
    outline: "none",
    borderColor: "primary.main",
  },
};

/* ── Sub-components ───────────────────────────────────────────────── */

function FilterSection({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: "error.main", ml: 0.5, fontSize: 14 }}
            >
              *
            </Typography>
          )}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 11, color: "grey.400", fontWeight: 500 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

function AndDivider() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
      <Typography
        sx={{
          px: 1.5,
          fontSize: 11,
          fontWeight: 700,
          color: "grey.400",
          letterSpacing: 1,
        }}
      >
        AND
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
    </Box>
  );
}

function OrChipGroup({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
      {children}
    </Box>
  );
}

function MultiSelectFilter<T extends string>({
  label,
  placeholder,
  options,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: { key: T; name: string }[];
  value: T[];
  onChange: (val: T[]) => void;
}) {
  return (
    <FilterSection label={label} hint="matches any">
      <Select
        multiple
        displayEmpty
        value={value}
        onChange={(e: SelectChangeEvent<T[]>) => {
          const val = e.target.value;
          onChange((typeof val === "string" ? val.split(",") : val) as T[]);
        }}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography sx={{ color: "rgba(1,67,57,0.5)", fontSize: 14 }}>
                {placeholder}
              </Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((v) => (
                <Chip
                  key={v}
                  label={options.find((o) => o.key === v)?.name || v}
                  size="small"
                />
              ))}
            </Box>
          );
        }}
        sx={selectSx}
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
      >
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            <Checkbox
              checked={value.includes(option.key)}
              size="small"
              sx={{ "&.Mui-checked": { color: "primary.main" } }}
            />
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
    </FilterSection>
  );
}

function CategorySelect({
  topLevelCategories,
  selectedCategoryIds,
  onToggle,
  onToggleAll,
}: {
  topLevelCategories: BentoCategory[];
  selectedCategoryIds: string[];
  onToggle: (subcategoryId: number, topLevelId: number) => void;
  onToggleAll: (category: BentoCategory) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        maxHeight: 420,
        overflowY: "auto",
      }}
    >
      {topLevelCategories.map((category) => {
        const subcategoryIds = category.subcategories?.map((s) => s.id) || [];
        const isAllSelected = selectedCategoryIds.includes(
          `${category.id}:all`,
        );
        const selectedCount = isAllSelected
          ? subcategoryIds.length
          : subcategoryIds.filter((id) => selectedCategoryIds.includes(`${id}`))
              .length;
        const isExpanded = expandedIds.has(category.id);

        return (
          <Box key={category.id}>
            <ListItemButton
              onClick={() => toggleExpand(category.id)}
              sx={{ py: 1, px: 2 }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "text.primary",
                  flex: 1,
                }}
              >
                {category.name}
                {selectedCount > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "primary.main",
                      backgroundColor: "rgba(1,67,57,0.08)",
                      borderRadius: "10px",
                      px: 1,
                      py: 0.25,
                    }}
                  >
                    {isAllSelected ? "All" : selectedCount}
                  </Typography>
                )}
              </Typography>
              <Box
                component="i"
                className={
                  isExpanded
                    ? "fa-solid fa-chevron-up"
                    : "fa-solid fa-chevron-down"
                }
                sx={{ fontSize: 12, color: "grey.500" }}
              />
            </ListItemButton>
            <Collapse in={isExpanded}>
              <Box sx={{ pl: 1 }}>
                {category.subcategories?.map((sub) => (
                  <ListItemButton
                    key={sub.id}
                    onClick={() => onToggle(sub.id, category.id)}
                    sx={{ py: 0.25, px: 1 }}
                  >
                    <Checkbox
                      checked={
                        isAllSelected ||
                        selectedCategoryIds.includes(`${sub.id}`)
                      }
                      disabled={isAllSelected}
                      size="small"
                      sx={{ "&.Mui-checked": { color: "primary.main" } }}
                    />
                    <ListItemText
                      primary={sub.name}
                      primaryTypographyProps={{
                        fontSize: 13,
                        color: "text.primary",
                      }}
                    />
                  </ListItemButton>
                ))}
                <ListItemButton
                  onClick={() => onToggleAll(category)}
                  sx={{ py: 0.25, px: 1 }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    size="small"
                    sx={{ "&.Mui-checked": { color: "primary.main" } }}
                  />
                  <ListItemText
                    primary="All"
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  />
                </ListItemButton>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

/* ── Main component ───────────────────────────────────────────────── */

interface ConfigureBentoBrandDatabaseProps {
  draftId: number;
  onNext: (config: Record<string, any>) => Promise<void>;
  initialConfig?: Record<string, any> | null;
}

export default function ConfigureBentoBrandDatabase({
  draftId: _draftId,
  onNext,
  initialConfig,
}: ConfigureBentoBrandDatabaseProps) {
  const { topLevelCategories, categoriesLoading, fetchBentoCategories } =
    useContext(BentoCategoriesContext);

  const initial = initialConfig?.search_parameters as
    | BentoBrandSearchParameters
    | undefined;

  const [searchTerms, setSearchTerms] = useState<string[]>(() => {
    if (!initial?.query) return [];
    return initial.query
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
  });
  const [searchTermInput, setSearchTermInput] = useState("");
  const [searchTermWarning, setSearchTermWarning] = useState<string | null>(
    null,
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    initial?.category_ids?.map(String) ?? [],
  );
  const [geopoints, setGeopoints] = useState<DiscoverSearchGeopoint[]>(() => {
    if (!initial?.geopoints) return [];
    return initial.geopoints.map((g) =>
      typeof g === "string" ? JSON.parse(g) : g,
    );
  });
  const [requireInstagram, setRequireInstagram] = useState(
    initial?.min_instagram_followers != null ||
      initial?.max_instagram_followers != null,
  );
  const [minFollowers, setMinFollowers] = useState<number | "">(
    initial?.min_instagram_followers ?? "",
  );
  const [maxFollowers, setMaxFollowers] = useState<number | "">(
    initial?.max_instagram_followers ?? "",
  );
  const [languages, setLanguages] = useState<Language[]>(
    (initial?.languages as Language[]) ?? [Language.en],
  );
  const [targetGenders, setTargetGenders] = useState<TargetGender[]>(
    (initial?.target_genders as TargetGender[]) ?? [],
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const categoriesErrorRef = useRef<HTMLElement>(null);
  const locationErrorRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (fieldErrors.categories) {
      categoriesErrorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (fieldErrors.location) {
      locationErrorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [fieldErrors]);

  useEffect(() => {
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Show the gender filter only when every selected category is gender-relevant.
   * A "select all" counts if the parent name is in GENDER_RELEVANT_PARENTS.
   * An individual subcategory counts if any of its parents is gender-relevant
   * OR if the subcategory name itself is in GENDER_RELEVANT_SUBCATEGORIES.
   */
  const showGenderFilter = useMemo(() => {
    if (selectedCategoryIds.length === 0) return false;

    const isSubcategoryRelevant = (subId: number): boolean => {
      for (const c of topLevelCategories) {
        const sub = c.subcategories?.find((s) => s.id === subId);
        if (!sub) continue;
        if (GENDER_RELEVANT_PARENTS.has(c.name)) return true;
        if (GENDER_RELEVANT_SUBCATEGORIES.has(sub.name)) return true;
      }
      return false;
    };

    return selectedCategoryIds.every((key) => {
      if (key.endsWith(":all")) {
        const parentId = parseInt(key.split(":")[0], 10);
        const parent = topLevelCategories.find((c) => c.id === parentId);
        return parent ? GENDER_RELEVANT_PARENTS.has(parent.name) : false;
      }
      return isSubcategoryRelevant(parseInt(key, 10));
    });
  }, [selectedCategoryIds, topLevelCategories]);

  // Clear gender selection when the filter is hidden
  useEffect(() => {
    if (!showGenderFilter && targetGenders.length > 0) {
      setTargetGenders([]);
    }
  }, [showGenderFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Category handlers ── */

  const toggleCategory = (subcategoryId: number, topLevelId: number) => {
    const key = `${subcategoryId}`;
    const allKey = `${topLevelId}:all`;
    setFieldErrors((prev) => {
      if (!prev.categories) return prev;
      const { categories: _, ...rest } = prev;
      return rest;
    });

    setSelectedCategoryIds((prev) => {
      if (prev.includes(allKey)) {
        const category = topLevelCategories.find((c) => c.id === topLevelId);
        const siblingKeys = (category?.subcategories || [])
          .map((s) => `${s.id}`)
          .filter((k) => k !== key);
        return [...prev.filter((x) => x !== allKey), ...siblingKeys];
      }

      return prev.includes(key)
        ? prev.filter((x) => x !== key)
        : [...prev, key];
    });
  };

  const toggleAllForCategory = (category: BentoCategory) => {
    const allKey = `${category.id}:all`;
    const subcategoryKeys = (category.subcategories || []).map(
      (s) => `${s.id}`,
    );
    setFieldErrors((prev) => {
      if (!prev.categories) return prev;
      const { categories: _, ...rest } = prev;
      return rest;
    });

    setSelectedCategoryIds((prev) => {
      if (prev.includes(allKey)) {
        return prev.filter((x) => x !== allKey);
      }
      return [...prev.filter((x) => !subcategoryKeys.includes(x)), allKey];
    });
  };

  /* ── Location handlers ── */

  const handleLocationChange = (userLocation: UserLocation) => {
    if (!userLocation?.latitude || !userLocation?.longitude) return;
    setFieldErrors((prev) => {
      if (!prev.location) return prev;
      const { location: _, ...rest } = prev;
      return rest;
    });

    const addressComponents = userLocation.address_components;
    const city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;
    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    const isCountry = !city && !region;
    const label = city || region || addressComponents?.[0]?.longText;

    const newGeopoint: DiscoverSearchGeopoint = {
      country_code: userLocation.country_code,
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      is_country: isCountry,
      label,
    };

    const isDuplicate = geopoints.some(
      (g) =>
        g.latitude === newGeopoint.latitude &&
        g.longitude === newGeopoint.longitude,
    );
    if (!isDuplicate) {
      setGeopoints((prev) => [...prev, newGeopoint]);
    }
  };

  const removeGeopoint = (index: number) => {
    setGeopoints((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── Build search params ── */

  const buildSearchParams = (
    finalSearchTerms?: string[],
  ): BentoBrandSearchParameters => {
    const terms = finalSearchTerms ?? searchTerms;
    const params: BentoBrandSearchParameters = {
      sort: Sort.Recommended,
    };

    if (terms.length > 0) params.query = terms.join(", ");

    if (selectedCategoryIds.length > 0) {
      const expandedIds: string[] = [];
      for (const key of selectedCategoryIds) {
        if (key.endsWith(":all")) {
          const parentId = parseInt(key.split(":")[0], 10);
          const parent = topLevelCategories.find((c) => c.id === parentId);
          if (parent?.subcategories) {
            expandedIds.push(...parent.subcategories.map((sub) => `${sub.id}`));
          }
        } else {
          expandedIds.push(key);
        }
      }
      params.category_ids = expandedIds;
    }

    if (geopoints.length > 0) {
      params.geopoints = geopoints.map((g) => JSON.stringify(g));
    }

    if (minFollowers !== "" && minFollowers > 0) {
      params.min_instagram_followers = minFollowers;
    }
    if (maxFollowers !== "" && maxFollowers !== -1) {
      params.max_instagram_followers = maxFollowers;
    }

    if (languages.length > 0) params.languages = languages;
    if (targetGenders.length > 0) params.target_genders = targetGenders;

    return params;
  };

  const handleNext = async () => {
    let finalSearchTerms = [...searchTerms];

    // Auto-add pending search term input (split by comma)
    const pendingRaw = searchTermInput.trim();
    if (pendingRaw) {
      const parts = pendingRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const tooLong = parts.some(
        (p) => p.split(/\s+/).filter(Boolean).length > 2,
      );
      if (tooLong) {
        setSearchTermWarning(
          "Each search term must be 1-2 words. Use commas to separate multiple terms.",
        );
        return;
      }
      for (const part of parts) {
        if (!finalSearchTerms.includes(part)) {
          finalSearchTerms = [...finalSearchTerms, part];
        }
      }
      setSearchTerms(finalSearchTerms);
      setSearchTermInput("");
      setSearchTermWarning(null);
    }

    // Validate required fields
    const errors: Record<string, string> = {};
    if (selectedCategoryIds.length === 0) {
      errors.categories = "Please select at least one category.";
    }
    if (geopoints.length === 0) {
      errors.location = "Please add at least one location.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      await onNext({ search_parameters: buildSearchParams(finalSearchTerms) });
    } catch {
      setLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    if (!searchTerms.includes(example)) {
      setSearchTerms((prev) => [...prev, example]);
    }
  };

  return (
    <Box sx={{ maxWidth: 976, mx: "auto" }}>
      {/* Source header */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: "purple.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-database"
            sx={{ fontSize: 18, color: "white" }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              color: "text.primary",
              lineHeight: "32px",
            }}
          >
            Bento Brand Database
          </Typography>
          <Typography
            sx={{ fontSize: 16, color: "text.secondary", lineHeight: "24px" }}
          >
            Search our comprehensive database of 10,000+ verified brands
          </Typography>
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* ── Left panel: form card ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            p: 3,
          }}
        >
          {/* Categories */}
          <FilterSection label="Categories" required hint="matches any">
            {categoriesLoading && topLevelCategories.length === 0 ? (
              <Box
                sx={{
                  border: "2px solid #e5e7eb",
                  borderRadius: "10px",
                  p: 1,
                }}
              >
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={36}
                    sx={{ borderRadius: "8px", mb: i < 5 ? 0.75 : 0 }}
                  />
                ))}
              </Box>
            ) : (
              <CategorySelect
                topLevelCategories={topLevelCategories}
                selectedCategoryIds={selectedCategoryIds}
                onToggle={toggleCategory}
                onToggleAll={toggleAllForCategory}
              />
            )}
            {fieldErrors.categories && (
              <Typography
                ref={categoriesErrorRef}
                sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}
              >
                {fieldErrors.categories}
              </Typography>
            )}
            {selectedCategoryIds.length > 0 && (
              <OrChipGroup>
                {selectedCategoryIds.map((key) => {
                  let label: string;
                  let onDelete: () => void;

                  if (key.endsWith(":all")) {
                    const topId = parseInt(key.split(":")[0], 10);
                    const cat = topLevelCategories.find((c) => c.id === topId);
                    label = cat ? `${cat.name} (All)` : key;
                    onDelete = () =>
                      cat
                        ? toggleAllForCategory(cat)
                        : setSelectedCategoryIds((prev) =>
                            prev.filter((x) => x !== key),
                          );
                  } else {
                    const subId = parseInt(key, 10);
                    const parent = topLevelCategories.find((c) =>
                      c.subcategories?.some((s) => s.id === subId),
                    );
                    const sub = parent?.subcategories?.find(
                      (s) => s.id === subId,
                    );
                    label = sub?.name || key;
                    onDelete = () => toggleCategory(subId, parent?.id ?? 0);
                  }

                  return (
                    <Chip
                      key={key}
                      label={label}
                      size="small"
                      onDelete={onDelete}
                    />
                  );
                })}
              </OrChipGroup>
            )}
          </FilterSection>

          {/* Search Terms */}
          <FilterSection label="Search Terms" hint="press Enter to add">
            <TextField
              fullWidth
              placeholder="e.g., sustainable, vegan, clean"
              value={searchTermInput}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTermInput(val);
                // Check each comma-separated part for word count
                const parts = val
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const anyTooLong = parts.some(
                  (p) => p.split(/\s+/).filter(Boolean).length > 2,
                );
                setSearchTermWarning(
                  anyTooLong
                    ? "Each search term must be 1-2 words. Use commas to separate multiple terms."
                    : null,
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const parts = searchTermInput
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  // Block if any term exceeds 2 words
                  const tooLong = parts.some(
                    (p) => p.split(/\s+/).filter(Boolean).length > 2,
                  );
                  if (tooLong) {
                    setSearchTermWarning(
                      "Each search term must be 1-2 words. Use commas to separate multiple terms.",
                    );
                    return;
                  }
                  const valid = parts.filter(
                    (p, i) =>
                      !searchTerms.includes(p) && parts.indexOf(p) === i,
                  );
                  if (valid.length > 0) {
                    setSearchTerms((prev) => [...prev, ...valid]);
                  }
                  setSearchTermInput("");
                  setSearchTermWarning(null);
                }
              }}
              error={!!searchTermWarning}
              sx={textFieldSx}
            />
            {searchTermWarning && (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                {searchTermWarning}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: "grey.500", mt: 0.5 }}>
              Short keywords to search our brand database
            </Typography>
            {searchTerms.length > 0 && (
              <OrChipGroup>
                {searchTerms.map((term, i) => (
                  <Chip
                    key={`term-${i}`}
                    label={term}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-solid fa-magnifying-glass"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() =>
                      setSearchTerms((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                  />
                ))}
              </OrChipGroup>
            )}
          </FilterSection>

          <AndDivider />

          {/* Location */}
          <FilterSection label="Location" required hint="matches any">
            <Box
              sx={{ mb: 1 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <LocationAutocomplete
                handleLocationChange={handleLocationChange}
                customStyling={locationAutocompleteSx}
                placeholderText="Search for a city, region, or country"
              />
            </Box>
            {fieldErrors.location && (
              <Typography
                ref={locationErrorRef}
                sx={{ fontSize: 12, color: "error.main", mb: 1 }}
              >
                {fieldErrors.location}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: "grey.500", mb: 1 }}>
              Add multiple locations — brands matching any location will be
              included
            </Typography>

            {geopoints.length > 0 && (
              <OrChipGroup>
                {geopoints.map((g, i) => (
                  <Chip
                    key={`geo-${i}`}
                    label={g.label || "Location"}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-solid fa-location-dot"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() => removeGeopoint(i)}
                  />
                ))}
              </OrChipGroup>
            )}
          </FilterSection>

          {/* Advanced Filters */}
          <Divider sx={{ borderColor: "#e5e7eb", mb: 2 }} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Advanced Filters
          </Typography>
          <Typography sx={{ fontSize: 12, color: "grey.400", mb: 2 }}>
            Each filter narrows results further.
          </Typography>

          {/* Instagram filter */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                mb: requireInstagram ? 1.5 : 0,
              }}
              onClick={() => {
                setRequireInstagram((v) => {
                  if (v) {
                    setMinFollowers("");
                    setMaxFollowers("");
                  }
                  return !v;
                });
              }}
            >
              <Checkbox
                checked={requireInstagram}
                size="small"
                sx={{ p: 0, "&.Mui-checked": { color: "primary.main" } }}
              />
              <Typography
                sx={{ fontSize: 14, color: "text.primary", fontWeight: 500 }}
              >
                Brand has an Instagram account
              </Typography>
            </Box>

            {requireInstagram && (
              <Box
                sx={{
                  pl: 3.5,
                  borderLeft: "2px solid #e5e7eb",
                  ml: 1.25,
                }}
              >
                <Typography sx={{ fontSize: 12, color: "grey.500", mb: 1 }}>
                  Optionally narrow by follower count
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Select<number | string>
                    displayEmpty
                    value={minFollowers}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMinFollowers(val === "" ? "" : Number(val));
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography
                          sx={{ color: "rgba(1,67,57,0.5)", fontSize: 14 }}
                        >
                          Min
                        </Typography>
                      ) : (
                        FOLLOWER_THRESHOLDS.find((t) => t.value === selected)
                          ?.label || `${selected}`
                      )
                    }
                    sx={{ ...selectSx, flex: 1 }}
                  >
                    {FOLLOWER_THRESHOLDS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography
                    sx={{ color: "grey.400", fontSize: 14, flexShrink: 0 }}
                  >
                    to
                  </Typography>

                  <Select<number | string>
                    displayEmpty
                    value={maxFollowers}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMaxFollowers(val === "" ? "" : Number(val));
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography
                          sx={{ color: "rgba(1,67,57,0.5)", fontSize: 14 }}
                        >
                          Max
                        </Typography>
                      ) : (
                        FOLLOWER_MAX_THRESHOLDS.find(
                          (t) => t.value === selected,
                        )?.label || `${selected}`
                      )
                    }
                    sx={{ ...selectSx, flex: 1 }}
                  >
                    {FOLLOWER_MAX_THRESHOLDS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}
          </Box>

          {/* Languages */}
          <MultiSelectFilter
            label="Languages"
            placeholder="Select languages"
            options={LANGUAGES}
            value={languages}
            onChange={setLanguages}
          />

          {/* Target Gender — only shown for gender-relevant categories */}
          {showGenderFilter && (
            <MultiSelectFilter
              label="Target Gender"
              placeholder="Any gender"
              options={GENDER_OPTIONS}
              value={targetGenders}
              onChange={setTargetGenders}
            />
          )}

          {/* Action buttons */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={
              <Box
                component="span"
                className="fa-solid fa-eye"
                sx={{ fontSize: 14 }}
              />
            }
            sx={{
              mt: 3,
              borderRadius: "10px",
              borderColor: "primary.main",
              borderWidth: 2,
              color: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
              mb: 1.5,
              "&:hover": {
                borderColor: "primary.main",
                borderWidth: 2,
                backgroundColor: "rgba(1,67,57,0.04)",
              },
            }}
          >
            Preview What This Search Would Return Today
          </Button>

          <LoadingButton
            variant="contained"
            fullWidth
            loading={loading}
            onClick={handleNext}
            sx={{
              borderRadius: "10px",
              backgroundColor: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
            }}
          >
            Next
          </LoadingButton>
        </Box>

        {/* ── Right panel: tips & examples ── */}
        <Box
          sx={{
            width: 310,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Tips card */}
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fee685",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}
            >
              <Box
                component="span"
                className="fa-solid fa-lightbulb"
                sx={{ fontSize: 18, color: "#7b3306" }}
              />
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#7b3306" }}
              >
                Tips
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {TIPS.map((tip, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: "#e17100",
                    mb: 1,
                    "&::marker": { color: "#e17100" },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, color: "#7b3306", lineHeight: "20px" }}
                  >
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* How filters work card */}
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534", mb: 1 }}
            >
              How filters work
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Box component="li" sx={{ color: "#15803d", mb: 0.5 }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Multiple options in one filter? Matches any of them (e.g. New
                  York or LA)
                </Typography>
              </Box>
              <Box component="li" sx={{ color: "#15803d" }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Different filter types narrow results — brands must match all
                  of them
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Examples card */}
          <Box
            sx={{
              backgroundColor: "#eff6ff",
              border: "1px solid #bedbff",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#1c398e", mb: 1.5 }}
            >
              Examples
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {EXAMPLE_SEARCHES.map((example) => (
                <Button
                  key={example}
                  onClick={() => handleExampleClick(example)}
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #bedbff",
                    borderRadius: "10px",
                    color: "#1c398e",
                    fontSize: 14,
                    textTransform: "none",
                    justifyContent: "flex-start",
                    px: 1.5,
                    py: 1,
                    "&:hover": { backgroundColor: "#f0f4ff" },
                  }}
                >
                  {example}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
